import { Component, computed, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivityLogView, SearchActivityLogsParameters, SearchUsersParameters, UserView } from '@api-proxies';
import { GridService } from '@app/services/grid.service';
import { ActivityLogService } from '@http/clients/activity-log.service';
import { UserService } from '@http/clients/user.service';
import { AwaiterService } from '@services/awaiter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { formatDateTimeDisplay } from '@utils/date-utils';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { DatepickerDropdownComponent, ModalService, SearchInputComponent } from 'ngx-lib';
import { CustomMenuItemComponent } from '../custom-menu-item/custom-menu-item.component';
import { GenericTypeAheadComponent, ITypeAheadService } from '../generic-type-ahead/generic-type-ahead.component';
import { ModalActivityLogDetailsComponent } from '../modals/modal-activity-log-details/modal-activity-log-details.component';
import { ACTIVITY_LOG_GRID_COLUMNS } from './activity-log-grid.config';

@Component({
    selector: 'app-activity-log-grid',
    templateUrl: './activity-log-grid.component.html',
    styleUrls: ['./activity-log-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        SearchInputComponent,
        DatepickerDropdownComponent,
        ServerSideGridComponent,
        GenericTypeAheadComponent,
        CustomMenuItemComponent
    ]
})
export class ActivityLogGridComponent {
    entityId = input<number | undefined>(undefined);
    entityTypeId = input<number | undefined>(undefined);

    activityLogsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    gridColumns = computed<ColDef[]>(() => this.buildGridColumns());

    userServiceFilter: ITypeAheadService<UserView, SearchUsersParameters>;

    filtersForm: FormGroup;

    private readonly formValueSignal = signal<any>(null);

    private readonly activityLogService = inject(ActivityLogService);
    private readonly awaiter = inject(AwaiterService);
    private readonly userService = inject(UserService);
    private readonly gridService = inject(GridService);
    private readonly modalService = inject(ModalService);

    constructor() {
        this.userServiceFilter = this.userService;
        this.noResults.set(true);
        this.isLoading.set(true);

        this.filtersForm = new FormGroup({
            searchText: new FormControl(''),
            user: new FormControl<UserView | null>(null),
            dateFrom: new FormControl<Date | null>(null),
            dateTo: new FormControl<Date | null>(null)
        });

        const formValueChangesSignal = toSignal(this.filtersForm.valueChanges, {
            initialValue: this.filtersForm.value
        });

        effect(() => {
            const formValue = formValueChangesSignal();
            if (formValue && this.activityLogsDatasource) {
                this.formValueSignal.set(formValue);
                void this.onFilterChanged();
            }
        });

        effect(() => {
            const entityId = this.entityId();
            const entityTypeId = this.entityTypeId();
            if (entityId && entityTypeId && !this.activityLogsDatasource) {
                this.initializeComponent();
            } else if (entityId && entityTypeId && this.activityLogsDatasource) {
                void this.refreshGridView();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.id.toString();

    initializeComponent(): void {
        const entityId = this.entityId();
        const entityTypeId = this.entityTypeId();
        if (!entityId || !entityTypeId) return;

        this.createDatasource();
    }

    async refreshGridView(): Promise<void> {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    async onFilterChanged(): Promise<void> {
        await this.awaiter.awaitAction('Loading Activity Log', async () => {
            await this.refreshGridView();
        });
    }

    async openMoreDetailsModal(activityLogData: ActivityLogView) {
        await this.modalService.open(ModalActivityLogDetailsComponent, {
            id: activityLogData.id
        });
    }

    private searchParamsBuilder(gridParams: any): SearchActivityLogsParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const formValue = this.formValueSignal() || this.filtersForm.value;

        const searchParams = {
            pageNumber,
            pageSize,
            filterText: formValue.searchText || '',
            entityId: this.entityId() ?? 0,
            entityTypeId: this.entityTypeId() ?? 0,
            dateFrom: formValue.dateFrom || undefined,
            dateTo: formValue.dateTo || undefined,
            userId: formValue.user?.id || undefined,
            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {})
        } as SearchActivityLogsParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.activityLogsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.activityLogService.search(searchParams);

                    if (response?.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching activity logs:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private buildGridColumns(): ColDef[] {
        const cellRendererParams: any = {
            label: 'See more details',
            action: (activityLogData: ActivityLogView) => this.openMoreDetailsModal(activityLogData)
        };

        const columns = [...ACTIVITY_LOG_GRID_COLUMNS];

        return columns.map((col) => {
            if (col.colId === 'moreDetails') return { ...col, cellRendererParams };
            if (col.colId === 'date')
                return { ...col, valueFormatter: (params) => formatDateTimeDisplay(params.value) };
            return col;
        });
    }
}
